// pages/UserAdmin/crm/CustomFieldsDialog.js
//
// Onde o supervisor define QUAIS campos personalizados a empresa guarda por
// contato ("valor do boleto", "produto contratado", "vencimento"). Os
// VALORES são preenchidos no drawer do contato, por qualquer membro.
//
// Mora num diálogo do CRM em vez de uma tela própria no menu de propósito:
// definir campo é coisa que se faz uma vez a cada muitos meses, e quem
// pensa nisso está justamente olhando a lista de contatos. Uma entrada fixa
// na barra lateral para isso seria ruído permanente por um uso raro.
//
// Só o supervisor (e o ADMIN da Cetto) abre — a mesma dupla que o backend
// exige em POST/PATCH/DELETE /custom-fields. O botão que abre o diálogo
// também só aparece para eles; a trava de verdade está no backend.

import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, Select, Stack,
  TextField, Typography,
} from '@mui/material';
import { FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import { CRM_THEME } from './theme';
import api from '../../../services/Api';

const SYNE = "'Syne', system-ui, sans-serif";

// Espelha models.CUSTOM_FIELD_TYPES. O tipo escolhe o campo de entrada e a
// dica de formato; o valor vai pro banco como texto de qualquer jeito.
const TYPES = [
  { value: 'TEXT', label: 'Texto', hint: 'Qualquer coisa — nome de produto, código, observação.' },
  { value: 'NUMBER', label: 'Número', hint: 'Quantidade, parcelas.' },
  { value: 'CURRENCY', label: 'Valor (R$)', hint: 'Boleto, mensalidade, total do pedido.' },
  { value: 'DATE', label: 'Data', hint: 'Vencimento, data da consulta.' },
];

export default function CustomFieldsDialog({ open, onClose, onChanged }) {
  const token = localStorage.getItem('token');
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState('TEXT');
  // Campo marcado para exclusão — a confirmação acontece na própria linha,
  // sem um segundo diálogo por cima deste.
  const [confirming, setConfirming] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/custom-fields', auth);
      setFields(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível carregar os campos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
      setNewLabel('');
      setNewType('TEXT');
      setConfirming(null);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusy(true);
    setError('');
    try {
      await api.post('/custom-fields', { label, field_type: newType }, auth);
      setNewLabel('');
      setNewType('TEXT');
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível criar o campo.');
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (field, label) => {
    const trimmed = label.trim();
    if (!trimmed || trimmed === field.label) return;
    setBusy(true);
    setError('');
    try {
      await api.patch(`/custom-fields/${field.id}`, { label: trimmed }, auth);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível renomear o campo.');
      await load();  // devolve o rótulo antigo ao input
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (field) => {
    setBusy(true);
    setError('');
    try {
      await api.delete(`/custom-fields/${field.id}`, auth);
      setConfirming(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível remover o campo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, color: CRM_THEME.text, pb: 1 }}>
        Campos personalizados
        <Typography variant="caption" sx={{ display: 'block', color: CRM_THEME.text3, fontWeight: 500 }}>
          O que a sua empresa guarda em cada contato, além do básico
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={22} sx={{ color: CRM_THEME.brand }} />
          </Stack>
        ) : (
          <Stack spacing={1.25} sx={{ mb: 2.5 }}>
            {fields.length === 0 && (
              <Typography variant="body2" sx={{ color: CRM_THEME.text3, py: 1 }}>
                Nenhum campo ainda. Crie o primeiro abaixo — ele aparece na
                aba Perfil de todos os contatos.
              </Typography>
            )}

            {fields.map((field) => (
              <Box
                key={field.id}
                sx={{
                  border: `1px solid ${CRM_THEME.border}`,
                  borderRadius: 2, p: 1.25, bgcolor: 'var(--ch-card)',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    defaultValue={field.label}
                    onBlur={(e) => handleRename(field, e.target.value)}
                    disabled={busy}
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-input': { fontSize: 13, py: 0.9 } }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: CRM_THEME.text3, whiteSpace: 'nowrap',
                      bgcolor: CRM_THEME.surface, px: 1, py: 0.4, borderRadius: 1,
                    }}
                  >
                    {TYPES.find((t) => t.value === field.field_type)?.label || field.field_type}
                  </Typography>
                  {/* Botão só de ícone precisa de nome acessível: sem ele,
                      leitor de tela anuncia "botão" e pronto — e são vários
                      iguais nesta lista. */}
                  <IconButton
                    size="small"
                    aria-label={
                      confirming === field.id
                        ? `Cancelar remoção de ${field.label}`
                        : `Remover campo ${field.label}`
                    }
                    onClick={() => setConfirming(confirming === field.id ? null : field.id)}
                    disabled={busy}
                    sx={{ color: confirming === field.id ? 'var(--ch-red-d)' : CRM_THEME.text3 }}
                  >
                    {confirming === field.id ? <FiX size={15} /> : <FiTrash2 size={15} />}
                  </IconButton>
                </Stack>

                {confirming === field.id && (
                  <Stack
                    direction="row" alignItems="center" justifyContent="space-between"
                    spacing={1} sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${CRM_THEME.border}` }}
                  >
                    {/* A mensagem importa: remover NÃO destrói os valores.
                        Sem dizer isso, ninguém remove um campo usado por
                        milhares de contatos, mesmo quando deveria. */}
                    <Typography variant="caption" sx={{ color: CRM_THEME.text2, lineHeight: 1.4 }}>
                      Some da tela dos contatos. Os valores já preenchidos ficam
                      guardados e voltam se você recriar o campo com o mesmo nome.
                    </Typography>
                    <Button
                      size="small" onClick={() => handleDelete(field)} disabled={busy}
                      sx={{ textTransform: 'none', color: 'var(--ch-red-d)', fontWeight: 700, flexShrink: 0 }}
                    >
                      Remover
                    </Button>
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        )}

        <Box sx={{ borderTop: `1px solid ${CRM_THEME.border}`, pt: 2 }}>
          <Typography variant="caption" sx={{ color: CRM_THEME.text3, fontWeight: 700, display: 'block', mb: 1 }}>
            NOVO CAMPO
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small" fullWidth autoFocus
              placeholder="Ex.: Valor do boleto"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              disabled={busy}
              inputProps={{ maxLength: 80 }}
            />
            <Select
              size="small" value={newType} onChange={(e) => setNewType(e.target.value)}
              disabled={busy} sx={{ minWidth: 140 }}
            >
              {TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13 }}>{t.label}</MenuItem>
              ))}
            </Select>
            <Button
              onClick={handleCreate}
              disabled={busy || !newLabel.trim()}
              startIcon={busy ? <CircularProgress size={13} /> : <FiPlus size={14} />}
              variant="contained" disableElevation
              sx={{
                textTransform: 'none', fontWeight: 700, bgcolor: CRM_THEME.brand,
                whiteSpace: 'nowrap', px: 2,
                '&:hover': { bgcolor: CRM_THEME.brandHover },
              }}
            >
              Criar
            </Button>
          </Stack>
          <Typography variant="caption" sx={{ color: CRM_THEME.text3, display: 'block', mt: 1 }}>
            {TYPES.find((t) => t.value === newType)?.hint}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}
                sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: CRM_THEME.text2 }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
